'use client';

import { useEffect, useState } from 'react';
import { Tag } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { subscriptionsApi } from '@/lib/api/subscriptions';
import { getMaximumSubscriptionDiscount } from '@/lib/subscription-price-presentation';
import { pricingModal } from '@/lib/stores/pricing-modal-store';
import { withAsyncTimeout } from '@/lib/async-timeout';
import { RailButton } from './GlobalNavigationRail';

let cachedDiscount: { percent: number | null; expiresAt: number } | null = null;
let pendingDiscount: Promise<number | null> | null = null;

function loadDiscount(): Promise<number | null> {
  if (cachedDiscount && cachedDiscount.expiresAt > Date.now()) return Promise.resolve(cachedDiscount.percent);
  if (pendingDiscount) return pendingDiscount;
  pendingDiscount = withAsyncTimeout(subscriptionsApi.getPlans(), 12_000, 'Pricing navigation lookup timed out').then(response => {
    if (response.error || response.data?.success !== true || !Array.isArray(response.data.plans) || !response.data.currency) {
      throw new Error('Pricing navigation catalog unavailable');
    }
    const percent = getMaximumSubscriptionDiscount(response.data.plans, response.data.currency);
    cachedDiscount = { percent, expiresAt: Date.now() + 300_000 };
    return percent;
  }).finally(() => { pendingDiscount = null; });
  return pendingDiscount;
}

export function PricingNavigationButton({ enabled }: { enabled: boolean }) {
  const t = useTranslations('nav');
  const pricingT = useTranslations('pricing');
  const [offer, setOffer] = useState<{ loading: boolean; percent: number | null }>(() => ({
    loading: enabled,
    // The first browser frame must match the server even if another route
    // shell already completed this module's shared catalog request.
    percent: null,
  }));
  useEffect(() => {
    setOffer({
      loading: enabled,
      percent: cachedDiscount && cachedDiscount.expiresAt > Date.now() ? cachedDiscount.percent : null,
    });
    if (!enabled) return;
    let active = true;
    setOffer(current => ({ ...current, loading: true }));
    async function load() {
      try {
        const percent = await loadDiscount();
        if (active) setOffer({ loading: false, percent });
      } catch {
        if (active) setOffer({ loading: false, percent: null });
      }
    }
    void load();
    return () => { active = false; };
  }, [enabled]);
  return <RailButton icon={Tag} label={t('pricing')}
    appearance={offer.percent === null ? 'navigation' : 'offer'}
    badge={offer.percent === null ? undefined : pricingT('navigationPercentOff', { percent: offer.percent })}
    onClick={() => pricingModal.open({ triggerReason: 'manual', defaultTab: 'upgrade' })} />;
}
